/// <reference types="vite/client" />
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { ColorRecord, LAB } from "./color-engine";

let client: SupabaseClient | null = null;

function getClient(): SupabaseClient {
  if (client) return client;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    throw new Error(
      "Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY environment variables."
    );
  }
  client = createClient(url, anonKey);
  return client;
}

interface ColorRow {
  id: string;
  shade_code: string;
  name: string;
  family_id: string;
  hex_code: string;
  l_val: number;
  a_val: number;
  b_val: number;
}

/**
 * Fetches the full 150-color catalog.
 *
 * Callers should cache this aggressively (React Query with
 * `staleTime: Infinity`, or equivalent) — see PERFORMANCE_UX_NOTES.md.
 * This function itself doesn't cache; caching is the caller's concern so
 * it composes with whatever data layer the app uses.
 */
export async function fetchColorCatalog(): Promise<ColorRecord[]> {
  const { data, error } = await getClient()
    .from("colors")
    .select("id, shade_code, name, family_id, hex_code, l_val, a_val, b_val")
    .order("shade_code", { ascending: true });

  if (error) throw new Error(`Failed to fetch color catalog: ${error.message}`);

  return (data as ColorRow[]).map(rowToColorRecord);
}

interface MatchRow {
  id: string;
  shade_code: string;
  name: string;
  hex_code: string;
  delta_e: number;
}

export interface RemoteMatch {
  id: string;
  shadeCode: string;
  name: string;
  hex: string;
  deltaE: number;
}

/**
 * Calls the server-side match_color_ciede2000 function (see
 * supabase-migration.sql). Returns raw matches with deltaE — callers
 * should run these through `bandForDeltaE` from color-engine.ts to get
 * the user-facing label, same as the client-side path, so both agree.
 */
export async function matchColor(target: LAB, limit = 3): Promise<RemoteMatch[]> {
  const { data, error } = await getClient().rpc("match_color_ciede2000", {
    target_l: target.l,
    target_a: target.a,
    target_b: target.b,
    result_limit: limit,
  });

  if (error) throw new Error(`Color match failed: ${error.message}`);

  return (data as MatchRow[]).map((row) => ({
    id: row.id,
    shadeCode: row.shade_code,
    name: row.name,
    hex: row.hex_code,
    deltaE: row.delta_e,
  }));
}

function rowToColorRecord(row: ColorRow): ColorRecord {
  return {
    id: row.id,
    shadeCode: row.shade_code,
    name: row.name,
    familyId: row.family_id,
    hex: row.hex_code,
    lab: { l: row.l_val, a: row.a_val, b: row.b_val },
  };
}
